import { io, Socket } from "socket.io-client"
import Swal from "sweetalert2"
import errors from "./errors"
import uniqid from "uniqid"
import { images } from "./data"
// Get socket URL from environment variable
// - If VITE_SOCKET_URL is set and non-empty, use it
// - If empty string (web-game build), return undefined (socket.io will use current origin)
// - In development, fallback to dev server
const getSocketUrl = (): string | undefined => {
  const envUrl = import.meta.env.VITE_SOCKET_URL
  // If explicitly set to a non-empty value, use it
  if (envUrl && envUrl.trim() !== "") {
    return envUrl
  }
  // If empty string or undefined in production, return undefined (web-game mode)
  if (import.meta.env.PROD) {
    return undefined
  }
  // Development fallback
  return "http://192.168.1.11:5000"
}

// Get host URL from environment variable
// - If VITE_HOST_URL is set and non-empty, use it
// - If empty string (web-game build), return empty string
// - In development, fallback to dev server
const getHostUrl = (): string => {
  const envUrl = import.meta.env.VITE_HOST_URL
  // If explicitly set (even if empty), use it
  if (envUrl !== undefined) {
    return envUrl
  }
  // If undefined in production, return empty string (web-game mode)
  if (import.meta.env.PROD) {
    return ""
  }
  // Development fallback
  return "http://192.168.1.11:5000"
}

const socketUrl = getSocketUrl()
export const socket: Socket<ServerToClientEvents, ClientToServerEvents> =
  socketUrl ? io(socketUrl) : io()
// : io("http://localhost:5000")
export const ORIGINAL_GAME_STORE_LINK =
  "https://store.steampowered.com/app/4010680/Dealmaster/"
export const toRem = (px: number) => px / 16 + "rem"
export const host = getHostUrl()
export const getUserCreds = (): IUserCreds => {
  return JSON.parse(localStorage.getItem("user") || "{}") as IUserCreds
}
// src/utils/debounce.ts
// src/utils/debounce.ts

export async function fetchBytes(url: string) {
  const res = await fetch(url) // or: fetch(url, { cache: 'no-cache' })
  await res.blob() // ensures it downloads now
}
export async function loadAndDecode(url: string) {
  await fetchBytes(url)
  await new Promise<void>((resolve, reject) => {
    const img = new Image()
    img.src = url
    img.onload = async () => {
      // @ts-ignore
      if (img.decode) {
        try {
          await img.decode()
        } catch {}
      }
      resolve()
    }
    img.onerror = () => reject(new Error(`Failed: ${url}`))
  })
}
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): T {
  let timeout: NodeJS.Timeout
  return function executedFunction(...args: Parameters<T>) {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  } as T
}

export const isMobile = () =>
  /Mobi|Android/i.test(navigator.userAgent) || window.innerHeight < 500
export const validateNewPasswords = (pass1: string, pass2: string) => {
  if (pass1.length < 8) {
    Swal.fire({
      ...errors.auth.shortPassword,
      icon: "error",
      heightAuto: false,
    })
    return false
  }
  // var strongRegex = new RegExp(
  //   "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})"
  // )
  // if (!strongRegex.test(pass1)) {
  //   Swal.fire({
  //     ...errors.auth.weakPassword,
  //     icon: "error",
  //     heightAuto: false,
  //   })
  //   return false
  // }
  if (pass1 !== pass2) {
    Swal.fire({
      ...errors.auth.passwordsNotMatch,
      icon: "error",
      heightAuto: false,
    })
    return false
  }
  return true
}
export const getRandomId = () => {
  return uniqid()
}

export const toggleItemInArray = <T>(arr: T[], givenItem: T): T[] =>
  arr.includes(givenItem)
    ? arr.filter((item) => item !== givenItem)
    : [...arr, givenItem]
export const getMinifiedSituation = (
  situation: IGameSituation
): IMinifiedSituation => {
  return {
    players: situation.players.map((player) => ({
      money: player.money,
      properties: player.properties as number[],
      position: player.position,
      order: player.order,
      isBankrupt: player.isBankrupt,
      canBuyPropertiesWithoutAuction: player.canBuyPropertiesWithoutAuction,
      cards: player.cards.map((card) => card.id) as ActionCardId[],
      stock: player.stock,
      role: player.role,
      stockMultiplier: player.stockMultiplier,
      turnsBeingBankrupt: player.turnsBeingBankrupt,
    })),
    turnsLeft: situation.turnsLeft,
    turn: situation.turn,
    activeAuctionPropertyOrder: situation.activeAuctionPropertyOrder,
    housesData: situation.housesData,
    moneyOnTable: situation.moneyOnTable,
  }
}

interface IMinifiedSituation {
  players: IMinifiedPlayer[]
  turnsLeft: number
  turn: string
  activeAuctionPropertyOrder: number | null
  housesData: PlayerPropertyHouseData[]
  moneyOnTable: number
}
interface PlayerPropertyHouseData {
  houses: number
  propertyOrder: number
}
interface IMinifiedPlayer {
  money: number
  properties: number[]
  position: number
  order: PlayerOrder
  isBankrupt: boolean
  canBuyPropertiesWithoutAuction: number
  cards: ActionCardId[]
  stock: number
  role: GameRoleId
  stockMultiplier: number
  turnsBeingBankrupt: number
}
